import { ArchiveError } from '../app/errors';
import { config } from '../config';
import {
  ARCHIVE_ENTITIES,
  ENTITY_SPECS,
  OMITTED_FIELDS,
  isDeniedSettingKey,
} from './fields';
import type { ArchiveEntity } from './fields';
import type { MatchFormat, MatchPurpose } from '../types';
import { SCOPE_ENTITIES } from './scope';
import type { ArchiveScope, ArchiveScopeKind } from './scope';
import { BLOB_PREFIX, DATA_ENTRY, LOG_ENTRY_PATH, MANIFEST_ENTRY, SCOPE_ID } from './serialize';
import { readZip } from './zip';

/**
 * **The reading half of the archive format — BAK-2's parser, and SYN-5's.**
 *
 * `serialize.ts` is the only producer of archive bytes; this is the only consumer of them. The two
 * are deliberately in the same layer and under the same import rule (§1.2: no `domain/`, no
 * `data/`), which is what lets §13.5's recovery screen and a future migration tool read an archive
 * without an open database — the same property that makes the recovery *export* possible, in the
 * other direction.
 *
 * ── What this module decides, and what it refuses to decide ──────────────────
 *
 * It decides **format** questions: is this a Touchline archive, can this bundle read its
 * `formatVersion`, which entity does each array belong to, which bytes belong to which blob id. It
 * decides nothing about **rows** — a row here is `unknown`, and `data/rowSchemas.ts` is where a row
 * becomes a row (design §4: "a repo-boundary runtime validator for imported rows"). Splitting it
 * that way is not tidiness: the row validator has to live next to the schema it validates, and the
 * schema lives in `data/`.
 *
 * ── Everything that can refuse, refuses BEFORE anything is destroyed ─────────
 *
 * BAK-2's import is **replace-only**. A parser that discovered a fatal problem halfway through
 * writing would have wiped a profile to import nothing. So every fatal condition throws here, in a
 * pure function, with no transaction open and nothing removed:
 *
 *   - the bytes are not a STORE-only Touchline zip, or an entry fails its CRC (`readZip`)
 *   - `manifest.json` is missing, is not JSON, or is not an archive manifest
 *   - `manifest.formatVersion` is **newer than this bundle** — SYN-9's negotiation, below
 *   - `data.json` is missing, is not JSON, or is not an object of arrays
 *
 * Everything else — an unexpected entry, an entity the manifest did not promise, a count that
 * disagrees with the array it describes, a blob file with no metadata row — is reported. §13.5's
 * damaged-profile reasoning applies in this direction too: a backup you cannot fully read is still a
 * backup, and refusing all of it to protest part of it loses the rest.
 *
 * ── SYN-9, and why the version gate is asymmetric ────────────────────────────
 *
 * SYN-9: *"Any archive the service holds remains retrievable and importable by a later client
 * version; version negotiation, where needed, happens client-side on import."* That is a guarantee
 * in one direction only — **older archive into a newer client** — and this module honours it by
 * accepting any `formatVersion` at or below its own.
 *
 * The other direction is refused, and §13.5 already says so in words: *"Caveat, stated on the
 * screen: an older client cannot import an archive containing newer-schema rows."* Importing them
 * anyway would write rows whose unknown fields this bundle drops on the next export — a silent,
 * unrecoverable truncation of the user's only backup. A refusal the user can act on ("update the
 * app") is strictly better, and because it is raised here it costs them nothing.
 */

/** What `readArchive` found that a caller should log (OBS-1) and surface (NFR-8). */
export interface ArchiveReadReport {
  /** Entry names in the container that this format does not define. Ignored, never guessed at. */
  readonly unexpectedEntries: readonly string[];
  /** `data.json` keys that are not `ArchiveEntity` values. Their rows are not read. */
  readonly unknownEntities: readonly string[];
  /** Entities the scope promises that `data.json` did not carry. Treated as empty. */
  readonly missingEntities: readonly ArchiveEntity[];
  /** `entity: declared vs actual` where `manifest.entityCounts` disagrees with `data.json`. */
  readonly countMismatches: readonly string[];
  /** `blobs/` files whose id has no `audioBlobs` metadata row. Bytes are kept and reported. */
  readonly blobsWithoutMetadata: readonly string[];
  /** Metadata rows with no `blobs/` file. The row is still imported; the bytes are gone. */
  readonly metadataWithoutBlobs: readonly string[];
  /**
   * §11.8 — setting keys this archive carried that no archive should contain.
   *
   * `backupCredential` is the one that exists. Reported here so the *import* refuses it for the same
   * reason the export refuses it, from the same declaration, rather than trusting that no archive
   * ever contains one.
   */
  readonly deniedSettingKeys: readonly string[];
  /**
   * §15.6 — entity names this archive carried in a **superseded shape**, dropped whole.
   *
   * `ourPlayers` at `formatVersion <= 3` is the one that exists. This is deliberately its own
   * field and not a `countMismatches` entry or a quarantine list, because both of those are the
   * archive-**corruption** vocabulary and this is a deliberate act: the rows are the old kind, not
   * damaged. Three shipped precedents for "present in the file, deliberately ignored" —
   * {@link ArchiveReadReport.carriedDebugLog}, {@link ArchiveReadReport.deniedSettingKeys} and
   * {@link ArchiveReadReport.unknownEntities} — and this reaches the plan preview by the same path
   * as the second of them.
   */
  readonly droppedLegacyEntities: readonly string[];
  /** True when `debug/log.ndjson` was present. It is never imported (§17.4) — only noted. */
  readonly carriedDebugLog: boolean;
}

export interface ParsedArchive {
  readonly formatVersion: number;
  readonly scope: ArchiveScope;
  /** `manifest.generatedFor` — a match id, or `'profile'` / `'all'`. */
  readonly generatedFor: string;
  /** Rows exactly as `data.json` carried them, per entity. Unvalidated: every row is `unknown`. */
  readonly entities: Readonly<Partial<Record<ArchiveEntity, readonly unknown[]>>>;
  /** Blob bytes by audio id, keyed on the basename before the first `.` — never the extension. */
  readonly blobs: ReadonlyMap<string, Uint8Array>;
  readonly report: ArchiveReadReport;
}

const decoder = new TextDecoder('utf-8', { fatal: false });

const ENTITY_SET: ReadonlySet<string> = new Set<string>(ARCHIVE_ENTITIES);

const SCOPE_KINDS: ReadonlySet<string> = new Set<string>(Object.keys(SCOPE_ENTITIES));

function parseJsonEntry(name: string, bytes: Uint8Array): unknown {
  try {
    return JSON.parse(decoder.decode(bytes)) as unknown;
  } catch (cause) {
    throw new ArchiveError(`"${name}" in this archive is not valid JSON.`, {
      cause,
      context: { entry: name },
    });
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Rebuilds `ArchiveScope` from the manifest.
 *
 * The kind and the id are read as a pair rather than independently: a `match` scope with no id, or
 * an `all` scope whose `generatedFor` is a ULID, is a manifest that does not describe any archive
 * this app produces, and guessing which half to trust is how the wrong rows get written.
 */
function readScope(kind: string, generatedFor: string): ArchiveScope {
  if (!SCOPE_KINDS.has(kind)) {
    throw new ArchiveError(
      `This archive declares scope "${kind}", which is not one this app produces.`,
      { context: { scope: kind, known: [...SCOPE_KINDS] } },
    );
  }
  if (kind === 'match') {
    if (generatedFor.length === 0) {
      throw new ArchiveError('This match archive does not say which match it holds.', {
        context: { scope: kind },
      });
    }
    return { kind: 'match', matchId: generatedFor };
  }
  const scopeKind = kind as Exclude<ArchiveScopeKind, 'match'>;
  if (generatedFor !== SCOPE_ID[scopeKind]) {
    throw new ArchiveError(
      `A ${kind} archive must be generated for "${SCOPE_ID[scopeKind]}"; this one says "${generatedFor}".`,
      { context: { scope: kind, generatedFor } },
    );
  }
  return scopeKind === 'profile' ? { kind: 'profile' } : { kind: 'all' };
}

interface Manifest {
  readonly formatVersion: number;
  readonly scope: ArchiveScope;
  readonly generatedFor: string;
  readonly entityCounts: Readonly<Record<string, number>>;
}

function readManifest(bytes: Uint8Array): Manifest {
  const parsed = parseJsonEntry(MANIFEST_ENTRY, bytes);
  if (!isRecord(parsed)) {
    throw new ArchiveError('This archive’s manifest is not an object.', {});
  }

  const { formatVersion, scope, generatedFor, entityCounts } = parsed;

  if (typeof formatVersion !== 'number' || !Number.isInteger(formatVersion)) {
    throw new ArchiveError(
      'This archive’s manifest carries no integer formatVersion, so there is no way to tell whether this app can read it.',
      { context: { formatVersion } },
    );
  }
  // SYN-9's negotiation, and the whole of it — the service never parses an archive (SYN-8), so the
  // version travels inside the opaque bytes and this is where it is honoured.
  if (formatVersion > config.ARCHIVE_FORMAT_VERSION) {
    throw new ArchiveError(
      `This backup was made by a newer version of Touchline (archive format ${formatVersion}; this app reads ${config.ARCHIVE_FORMAT_VERSION}). Update the app, then import it again — nothing on this device has been changed.`,
      { context: { formatVersion, supported: config.ARCHIVE_FORMAT_VERSION } },
    );
  }

  if (typeof scope !== 'string' || typeof generatedFor !== 'string') {
    throw new ArchiveError('This archive’s manifest does not say what it covers.', {
      context: { scope, generatedFor },
    });
  }

  const counts: Record<string, number> = {};
  if (isRecord(entityCounts)) {
    for (const [key, value] of Object.entries(entityCounts)) {
      if (typeof value === 'number') counts[key] = value;
    }
  }

  return {
    formatVersion,
    scope: readScope(scope, generatedFor),
    generatedFor,
    entityCounts: counts,
  };
}

/**
 * Restores the fields `fields.ts` deliberately did not publish, as `null`.
 *
 * `OMITTED_FIELDS` is the export's declaration of what it leaves out; reading it here is what keeps
 * the two directions from drifting (`bidirectional-analysis`). Without this the round trip cannot
 * even start: `MatchSchema` requires `lastSyncedTs` and `lastSyncedHash` to be *present* and
 * nullable, and the archive carries neither — every match row would fail validation.
 *
 * `null` is the correct value rather than a convenient one. §17.4: both fields "are written **after**
 * the archive is built and only on a 2xx", so on a restored profile they describe an event that has
 * not happened. A restored match reads as never-synced *from this device*, which is the truth, and
 * §11.3's first drain re-establishes it against the server manifest.
 *
 * Every omitted field must therefore be nullable; `import.test.ts` asserts that against the row
 * schemas rather than leaving it as an assumption in this comment.
 */
function restoreOmittedFields(entity: ArchiveEntity, row: unknown): unknown {
  const omitted = OMITTED_FIELDS[entity];
  if (omitted.length === 0 || !isRecord(row)) return row;
  const restored: Record<string, unknown> = { ...row };
  for (const field of omitted) {
    if (!(field in restored)) restored[field] = null;
  }
  return restored;
}

/**
 * The `formatVersion` at which `Match.format` first appears in `MATCH_FIELDS` (§11.7a).
 *
 * Declared here rather than in `config/`, and **not** as `ARCHIVE_FORMAT_VERSION`: this is a fact
 * about the format's history that must never move, while `ARCHIVE_FORMAT_VERSION` is the version
 * this bundle *writes* and will bump again. Reading the current version here would silently disable
 * the backfill the moment a `3` shipped.
 */
const MATCH_FORMAT_ARCHIVE_VERSION = 2;

/**
 * §11.7a — the value every pre-`format` archive's matches are restored with.
 *
 * Typed against `types/`'s `MatchFormat` (which `archive/` may import, and `domain/` is not needed
 * for) so the literal is checked rather than spelled: if `'11v11'` ever left `MATCH_FORMATS` this
 * would stop compiling instead of quietly writing a value nothing offers.
 *
 * **Behaviour-preserving by construction.** Every `formationGuess` that could possibly be in such an
 * archive was chosen from the 11v11 list, because that was the only list that existed — so after the
 * backfill every restored guess is still offerable by its own match's format, and `MatchSchema`'s
 * cross-field refinement is what checks that rather than leaving it as an argument.
 */
const PRE_FORMAT_ARCHIVE_FORMAT: MatchFormat = '11v11';

/**
 * **The import is a second migration surface, and without this it loses whole matches.**
 *
 * §13.3's `v003` upgrades the rows on *this* device. SYN-5 restore and BAK-2 import read rows out of
 * a **file** written before the column existed, and those rows go straight to the repo-boundary
 * validator. With `format` required, `MatchSchema` rejects every one of them — and `validateImport`
 * quarantines a failing `Match` **together with its dependents**. The observable failure is not "a
 * field is missing"; it is a restore that silently drops whole matches, hypotheses, observations and
 * audio, reported as a quarantine list.
 *
 * Applied by the same reasoning as {@link restoreOmittedFields}: the export declares what it did not
 * carry — here through its `formatVersion` — and the import reads that declaration rather than
 * guessing. A row that already carries a `format` is left exactly as it is, so a hand-edited or
 * mixed archive is never overwritten by this.
 */
function backfillMatchFormat(formatVersion: number, entity: ArchiveEntity, row: unknown): unknown {
  if (formatVersion >= MATCH_FORMAT_ARCHIVE_VERSION) return row;
  if (entity !== 'matches' || !isRecord(row) || 'format' in row) return row;
  return { ...row, format: PRE_FORMAT_ARCHIVE_FORMAT };
}

/**
 * The `formatVersion` at which `Match.purpose` first appears in `MATCH_FIELDS` (B §4.4).
 *
 * Same reasoning as {@link MATCH_FORMAT_ARCHIVE_VERSION}: a fact about the format's history
 * that must never move, distinct from the `ARCHIVE_FORMAT_VERSION` this bundle writes.
 */
const MATCH_PURPOSE_ARCHIVE_VERSION = 3;

/**
 * B §4.4 / F-2 (ruled) — the value every pre-`purpose` archive's matches are restored with.
 *
 * Typed against `types/`'s `MatchPurpose` so the literal is checked rather than spelled.
 * Behaviour-preserving by the same argument as migration `v004`'s backfill: every match such
 * an archive can hold was an A match, and every A match ran the team-facing workflow — which
 * is what `'teamMatch'` means. **This is what lets an A export seed B unchanged** (scenario
 * 35): without it, `MatchSchema` (which requires `purpose` present) would quarantine every
 * imported A match together with its hypotheses, observations and audio.
 */
const PRE_PURPOSE_ARCHIVE_PURPOSE: MatchPurpose = 'teamMatch';

/**
 * The import-side half of migration `v004`, exactly {@link backfillMatchFormat}'s shape: the
 * export declares what it did not carry through its `formatVersion`, and the import reads
 * that declaration rather than guessing. A row that already carries a `purpose` is left
 * exactly as it is.
 */
function backfillMatchPurpose(formatVersion: number, entity: ArchiveEntity, row: unknown): unknown {
  if (formatVersion >= MATCH_PURPOSE_ARCHIVE_VERSION) return row;
  if (entity !== 'matches' || !isRecord(row) || 'purpose' in row) return row;
  return { ...row, purpose: PRE_PURPOSE_ARCHIVE_PURPOSE };
}

/**
 * The `formatVersion` at which `ourPlayers` first appears in its **reshaped** form (§15.3/§15.6).
 *
 * Same reasoning as {@link MATCH_FORMAT_ARCHIVE_VERSION} and {@link MATCH_PURPOSE_ARCHIVE_VERSION}:
 * a fact about the format's history that must never move, distinct from the
 * `ARCHIVE_FORMAT_VERSION` this bundle writes.
 */
const OUR_PLAYER_RESHAPE_ARCHIVE_VERSION = 4;

/**
 * §15.6 — an archive at `formatVersion <= 3` carries old-shape roster rows, and **there is no
 * backfill that would be honest.**
 *
 * The two precedents above repair a *missing* field with a value every such archive provably had
 * (`'11v11'`, `'teamMatch'`). Here the missing fields are a jersey number, a position and nineteen
 * ratings, and a free-text blob yields none of them. A synthesised row would be strictly worse than
 * no row, because §3.7.36 makes a secondary position **permission** to move a player — so a guessed
 * roster would manufacture exactly the licence that loses a match.
 *
 * So the entity is dropped **whole**, and at entity grain rather than per row, because a drop cannot
 * be expressed in the per-row backfill signature. Applied **before** `missingEntities` and
 * `countMismatches` are computed: leaving it to those would print *"ourPlayers: manifest 16, data
 * 0"* — the archive-corruption vocabulary — for something deliberate.
 *
 * Mutates `entities` to an **empty array** rather than deleting the key, so the entity is "present
 * and empty" (nothing to import) instead of "missing" (which `missingEntities` would then report).
 *
 * @returns the entity names dropped, for `ArchiveReadReport.droppedLegacyEntities`.
 */
function dropLegacyRoster(
  formatVersion: number,
  entities: Partial<Record<ArchiveEntity, readonly unknown[]>>,
): string[] {
  if (formatVersion >= OUR_PLAYER_RESHAPE_ARCHIVE_VERSION) return [];
  if (entities.ourPlayers === undefined || entities.ourPlayers.length === 0) return [];
  entities.ourPlayers = [];
  return ['ourPlayers'];
}

/** The audio id a `blobs/` entry belongs to: the basename before the first `.` (a ULID has none). */
function blobIdFromEntryName(name: string): string {
  const basename = name.slice(BLOB_PREFIX.length);
  const dot = basename.indexOf('.');
  return dot === -1 ? basename : basename.slice(0, dot);
}

/**
 * Reads one archive.
 *
 * Pure and total over its report: nothing is written, nothing is opened, and the only failures are
 * the ones that make the file unreadable as a whole (see the module note).
 *
 * @throws {ArchiveError} when the container is not a Touchline archive, an entry fails its CRC, the
 *   manifest is missing or unreadable, `data.json` is missing or unreadable, or the archive's
 *   `formatVersion` is newer than this bundle can read (SYN-9).
 */
export function readArchive(bytes: Uint8Array): ParsedArchive {
  const records = readZip(bytes);
  const byName = new Map(records.map((record) => [record.name, record.data]));

  const manifestBytes = byName.get(MANIFEST_ENTRY);
  if (!manifestBytes) {
    throw new ArchiveError(
      `This file has no ${MANIFEST_ENTRY}, so it is not a Touchline backup.`,
      { context: { entries: records.length } },
    );
  }
  const manifest = readManifest(manifestBytes);

  const dataBytes = byName.get(DATA_ENTRY);
  if (!dataBytes) {
    throw new ArchiveError(`This backup has no ${DATA_ENTRY}; there are no rows to import.`, {
      context: { entries: records.map((record) => record.name) },
    });
  }
  const data = parseJsonEntry(DATA_ENTRY, dataBytes);
  if (!isRecord(data)) {
    throw new ArchiveError(`${DATA_ENTRY} is not an object of entity arrays.`, {});
  }

  const entities: Partial<Record<ArchiveEntity, readonly unknown[]>> = {};
  const unknownEntities: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (!ENTITY_SET.has(key)) {
      unknownEntities.push(key);
      continue;
    }
    const entity = key as ArchiveEntity;
    if (!Array.isArray(value)) {
      unknownEntities.push(key);
      continue;
    }
    entities[entity] = value.map((row) =>
      restoreOmittedFields(
        entity,
        backfillMatchPurpose(
          manifest.formatVersion,
          entity,
          backfillMatchFormat(manifest.formatVersion, entity, row),
        ),
      ),
    );
  }

  // §15.6, obligation 1 — BEFORE `missingEntities` and `countMismatches`, both of which speak the
  // archive-corruption vocabulary and would describe a deliberate drop as damage.
  const droppedLegacyEntities = dropLegacyRoster(manifest.formatVersion, entities);
  const dropped: ReadonlySet<string> = new Set(droppedLegacyEntities);

  const expected = SCOPE_ENTITIES[manifest.scope.kind];
  const missingEntities = expected.filter((entity) => entities[entity] === undefined);

  const countMismatches: string[] = [];
  for (const [entity, declared] of Object.entries(manifest.entityCounts)) {
    if (!ENTITY_SET.has(entity)) continue;
    if (dropped.has(entity)) continue;
    const actual = entities[entity as ArchiveEntity]?.length ?? 0;
    if (actual !== declared) countMismatches.push(`${entity}: manifest ${declared}, data ${actual}`);
  }

  // §11.8, from the import side, and **only for a key this format has declared denied**.
  //
  // A `backupCredential` inside an archive means the file was produced by something that did not
  // apply the rule — a hand-edited zip, or a build with the exclusion broken — and the value in it
  // authenticates the backup service. It is dropped here, before it can reach a row.
  //
  // An *unrecognised* key is deliberately NOT dropped here even though `settingDisposition()` would
  // deny it: that is a newer client's preference, not a leaked secret, and its right answer is the
  // repo-boundary quarantine, which reports it by name so the user knows what was lost. See
  // `isDeniedSettingKey` for why the two directions are asymmetric.
  const deniedSettingKeys: string[] = [];
  const settings = entities.settings;
  if (settings) {
    const kept = settings.filter((row) => {
      const key = isRecord(row) && typeof row.key === 'string' ? row.key : null;
      if (key !== null && isDeniedSettingKey(key)) {
        deniedSettingKeys.push(key);
        return false;
      }
      return true;
    });
    entities.settings = kept;
  }

  const blobs = new Map<string, Uint8Array>();
  const unexpectedEntries: string[] = [];
  let carriedDebugLog = false;
  for (const record of records) {
    if (record.name === MANIFEST_ENTRY || record.name === DATA_ENTRY) continue;
    if (record.name === LOG_ENTRY_PATH) {
      // OBS-2 puts the ring inside the zip and §17.4 keeps it out of the comparison, "because the
      // import writes log entries to it *while importing*". Restoring it would overwrite this
      // import's own diagnostic record with the previous device's — noted, never read.
      carriedDebugLog = true;
      continue;
    }
    if (record.name.startsWith(BLOB_PREFIX)) {
      const id = blobIdFromEntryName(record.name);
      if (id.length > 0) {
        blobs.set(id, record.data);
        continue;
      }
    }
    unexpectedEntries.push(record.name);
  }

  const metadata = entities.audioBlobs ?? [];
  const metadataIds = new Set(
    metadata
      .map((row) => (isRecord(row) && typeof row.id === 'string' ? row.id : null))
      .filter((id): id is string => id !== null),
  );

  return {
    formatVersion: manifest.formatVersion,
    scope: manifest.scope,
    generatedFor: manifest.generatedFor,
    entities,
    blobs,
    report: {
      unexpectedEntries: unexpectedEntries.sort(),
      unknownEntities: unknownEntities.sort(),
      missingEntities,
      countMismatches,
      blobsWithoutMetadata: [...blobs.keys()].filter((id) => !metadataIds.has(id)).sort(),
      metadataWithoutBlobs: [...metadataIds].filter((id) => !blobs.has(id)).sort(),
      deniedSettingKeys: deniedSettingKeys.sort(),
      droppedLegacyEntities,
      carriedDebugLog,
    },
  };
}

/**
 * The primary key of one entity, as `ENTITY_SPECS` already declares it for the sort.
 *
 * Re-exported as a named reader because the importer needs the same answer for a different purpose
 * — deduplicating rows across the archives of a SYN-5 restore (§11.9: "Team and Player rows
 * deduplicate by id") — and two definitions of "this entity's key" would eventually disagree about
 * `settings`, the one entity whose key is not `id`.
 */
export function entityKeyField(entity: ArchiveEntity): string {
  return ENTITY_SPECS[entity].sortBy;
}
